// ./src/pages/private/EventSchedulePage/SlotSpeakersField.tsx
//
// "Who is on this session?" — pick people from the event's TEAM (speakers, hosts, performers, staff…) to show under the
// session on the public schedule (owner, 2026-09-30). The picked people are listed below the picker in the order they
// were added, which is the order the schedule shows them; each can be taken off.
//
// ⚠ The offer is exactly what the SERVER accepts (`EventTimeSlotCore.checkSpeakers` → `EVENT_CREW_ROLES`): the team
//   roles WITHOUT an act's touring `crew` and `entourage` (they belong to an artist booking, not the event's team), live
//   rows only. The app's own `TEAM_ROLES` includes those two — offering them would let a host pick someone the save then
//   refuses. The list and the pick-order rule live in `slotSpeakerRules.ts`; `check-slot-speakers` runs them.
// ⚠ A person already on the session but not in the team any more (removed, or made a plain guest) is not returned by
//   the server either (`speakerGuestIds` lists only showable people), so nothing stale can sit in the value.

import React, { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Avatar, Group, Input, Paper, Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { STATUSES } from '@sdflc/utils';
import { useTranslation } from 'react-i18next';

import { EventGuest, EventGuestService, getGuestDisplayName, getGuestRoleLabel } from '@/utils/api';
import { guestPhotoUrl } from '@/utils/guestPhoto';
import { AppIcon, CheckableMultiSelect, ErrorAlert } from '@/components/generic';

import { SESSION_PEOPLE_ROLES, appendPicks } from './slotSpeakerRules';

export interface SlotSpeakersFieldProps {
  eventId: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  error?: React.ReactNode;
}

export const SlotSpeakersField: React.FC<SlotSpeakersFieldProps> = ({ eventId, value, onChange, disabled = false, error }) => {
  const { t } = useTranslation(['events', 'common']);
  const [team, setTeam] = useState<EventGuest[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [readError, setReadError] = useState<OpResult | null>(null);

  useEffect(() => {
    let alive = true;

    setLoaded(false);
    EventGuestService.listAll({ eventId: [eventId], guestRole: [...SESSION_PEOPLE_ROLES], status: [STATUSES.ACTIVE] }).then((res) => {
      if (!alive) {
        return;
      }

      setReadError(res.didSucceed() ? null : res);
      setTeam(res.didSucceed() && Array.isArray(res.data) ? (res.data as EventGuest[]) : []);
      setLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, [eventId]);

  const byId = useMemo(() => new Map(team.filter((g) => !!g.id).map((g) => [g.id as string, g])), [team]);
  const options = useMemo(
    () =>
      team
        .filter((g) => !!g.id)
        .map((g) => ({ value: g.id as string, label: `${getGuestDisplayName(g)} · ${getGuestRoleLabel(g.guestRole)}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [team],
  );

  const handlePick = (next: string[]) => onChange(appendPicks(value, next));

  // ⚠ EVERY id in the value gets a row — one the list cannot name (a failed read, or a crew editor who may not list
  //   guests) is still shown and can still be taken off, never silently kept or silently dropped.
  const picked = value.map((id) => ({ id, guest: byId.get(id) ?? null }));

  return (
    <Stack gap="xs">
      <CheckableMultiSelect
        label={t('events:schedulePage.speakers', { defaultValue: 'Speakers' })}
        placeholder={
          // ⚠ Only a read that WORKED and found nobody says so — and without telling anyone where to add people: a crew
          //   editor who may not list guests gets the same empty answer.
          loaded && !readError && options.length === 0
            ? t('events:schedulePage.speakersNone', { defaultValue: 'No team members to pick from' })
            : t('events:schedulePage.speakersPh', { defaultValue: 'Pick from your team' })
        }
        data={options}
        value={value}
        onChange={handlePick}
        disabled={disabled || !loaded || options.length === 0}
      />
      <Text size="md" c="dimmed">
        {t('events:schedulePage.speakersHint', {
          defaultValue: 'Shown under this session on your event page, with their photo — in the order you add them.',
        })}
      </Text>
      {readError ? (
        <ErrorAlert error={readError} title={t('events:schedulePage.speakersLoadFailed', { defaultValue: 'Could not load your team' })} page="Schedule" operation="eventGuestList" />
      ) : null}
      {error ? <Input.Error size="md">{error}</Input.Error> : null}
      {picked.length > 0 ? (
        <Stack gap={6}>
          {picked.map(({ id, guest: g }) => (
            <Paper key={id} withBorder radius="md" p="xs">
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Avatar src={g ? guestPhotoUrl(g) : null} radius="xl" size={32} name={g ? getGuestDisplayName(g) : undefined} />
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="md" fw={600} truncate>
                      {g ? getGuestDisplayName(g) : t('events:schedulePage.speakerUnknown', { defaultValue: 'A team member' })}
                    </Text>
                    {g ? (
                      <Text size="md" c="dimmed">
                        {getGuestRoleLabel(g.guestRole)}
                      </Text>
                    ) : null}
                  </Stack>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  disabled={disabled}
                  aria-label={t('events:schedulePage.speakerRemove', { defaultValue: 'Take off this session' })}
                  onClick={() => onChange(value.filter((other) => other !== id))}
                >
                  <AppIcon iconName="X" size={18} />
                </ActionIcon>
              </Group>
            </Paper>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};
